// Core data types for the gym tracker app

export interface User {
  id: string;
  sessionId: string;
  createdAt: string;
}

export interface GymCheckin {
  id: string;
  userId: string;
  date: string;
  timestamp: string;
  createdAt: string;
}

export interface ExerciseLog {
  id: string;
  userId: string;
  date: string;
  exercise: string;
  weight: number;
  reps: number;
  sets: number;
  notes?: string;
  isPersonalRecord: boolean;
  timestamp: string;
  createdAt: string;
}

// Enhanced ProgressPhoto interface for image progress tracking
export interface ProgressPhoto {
  id: string;
  userId: string;
  fileKey: string;
  originalFilename?: string;
  fileSize: number;
  mimeType: string;
  width?: number;
  height?: number;
  uploadDate: string;
  photoDate: string;
  notes?: string;
  thumbnailKey?: string;
  createdAt: string;
  updatedAt: string;
  // Runtime properties
  url?: string;           // Signed R2 URL
  thumbnailUrl?: string;  // Signed thumbnail URL
}

// Photo upload and management types
export interface PhotoUploadRequest {
  file: File;
  photoDate?: string;
  notes?: string;
}

export interface PhotoListResponse {
  photos: ProgressPhoto[];
  totalCount: number;
  hasMore: boolean;
  nextCursor?: string;
}

export interface CustomExercise {
  id: string;
  userId: string;
  name: string;
  category: string;
  createdAt: string;
}

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

// Statistics types
export interface WorkoutStats {
  gymStreak: number;
  thisMonthGymVisits: number;
  thisMonthWorkouts: number;
  totalWorkouts: number;
  // Weekly progress data
  currentWeekVisits: number;
  currentWeekTarget: number;
  daysLeftInWeek: number;
}

// Generic Habit Tracking types
export type HabitFrequencyType = 'daily' | 'weekly' | 'monthly';

export interface Habit {
  id: string;
  userId: string;
  title: string;
  description?: string;
  icon: string;
  frequencyType: HabitFrequencyType;
  frequencyTarget: number;
  gracePeriodHours: number;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export interface HabitCheckin {
  id: string;
  habitId: string;
  userId: string;
  date: string;
  timestamp: string;
  notes?: string;
  durationMinutes?: number;
  createdAt: string;
}

export interface HabitCommitment {
  id: string;
  habitId: string;
  userId: string;
  date: string;
  committed: boolean;
  completed: boolean;
  createdAt: string;
  completedAt?: string;
}

export interface HabitStats {
  habitId: string;
  habitTitle: string;
  streak: number;
  currentPeriodCheckins: number;
  currentPeriodTarget: number;
  currentPeriodProgress: number; // percentage 0-100
  totalCheckins: number;
  lastCheckinDate?: string;
}

export interface CreateHabitRequest {
  title: string;
  description?: string;
  icon?: string;
  frequencyType: HabitFrequencyType;
  frequencyTarget: number;
  gracePeriodHours?: number;
  color?: string;
}

export interface UpdateHabitRequest {
  title?: string;
  description?: string;
  icon?: string;
  frequencyTarget?: number;
  gracePeriodHours?: number;
  color?: string;
  isActive?: boolean;
}

export interface HabitCheckinRequest {
  date?: string;
  notes?: string;
  durationMinutes?: number;
}

export interface ExerciseProgress {
  exercise: string;
  sessions: Array<{
    date: string;
    weight: number;
    timestamp: string;
  }>;
  latestWeight: number;
  improvement?: number;
}

// Exercise database - Standardized naming conventions
export const EXERCISES = [
  // Upper Body - Push (Chest, Shoulders, Triceps)
  'Bench Press Machine',
  'Incline Dumbbell Press', 
  'Chest Press Machine',
  'Pec Deck Machine',
  'Cable Chest Fly',
  'Shoulder Press Machine',
  'Dumbbell Shoulder Press',
  'Lateral Raise',
  'Cable Lateral Raise',
  'Rear Delt Fly',
  'Triceps Pushdown',
  'Overhead Triceps Extension',
  'Triceps Machine',
  'Dips Machine',
  
  // Upper Body - Pull (Back, Biceps)
  'Lat Pulldown',
  'Pull Ups',
  'Seated Cable Row',
  'T-Bar Row',
  'Cable Biceps Curl',
  'Dumbbell Biceps Curl',
  'Hammer Curl',
  'Preacher Curl',
  'Reverse Fly',
  
  // Lower Body - Quad Dominant
  'Leg Press',
  'Hack Squat',
  'Squats',
  'Leg Extension',
  'Bulgarian Split Squats',
  
  // Lower Body - Hip Dominant
  'Romanian Deadlift',
  'Hip Thrust',
  'Lying Leg Curl',
  'Seated Leg Curl',
  'Cable Glute Kickback',
  
  // Lower Body - Calves
  'Standing Calf Raise',
  'Seated Calf Raise',
  'Calf Press',
  
  // Core & Abs
  'Ab Crunch Machine',
  'Cable Crunch',
  'Russian Twists',
  'Rotary Torso Machine',
  'Hip Abduction',
  'Hip Adduction',
] as const;

export type Exercise = typeof EXERCISES[number];